import * as React from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ApiError, UnauthorizedError } from "@/api/errors";
import { requestPasswordReset } from "@/api/auth";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PortalSignInError, portalSignInAvailable, startPortalSignIn } from "./portal";
import { useSession } from "./session";

const schema = z.object({
  account: z.string().min(1, "Account name is required"),
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
});
type Values = z.infer<typeof schema>;

export function LoginPage() {
  const status = useSession((s) => s.status);
  const login = useSession((s) => s.login);
  const completeMfa = useSession((s) => s.completeMfa);
  const pendingMfa = useSession((s) => s.pendingMfa);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/";
  const [error, setError] = React.useState<string>();
  const [code, setCode] = React.useState("");
  const [mode, setMode] = React.useState<"login" | "forgot" | "sent">("login");
  const withVonix = portalSignInAvailable();

  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { account: "", username: "", password: "" } });

  if (status === "authenticated") return <Navigate to={from} replace />;

  const describe = (err: unknown): string => {
    if (err instanceof UnauthorizedError) {
      switch (err.kind) {
        case "invalid_credentials":
          return "That account, username and password combination is not right.";
        case "account_locked":
          return "This account is locked after too many failed sign-ins. An admin can unlock it from Account settings.";
        case "expired":
          return "The sign-in was refused. Check the account name.";
        default:
          return err.message;
      }
    }
    if (err instanceof ApiError) return `${err.message} (request ${err.requestId ?? "n/a"})`;
    return "Could not reach the API.";
  };

  const onSubmit = async (v: Values) => {
    setError(undefined);
    try {
      const result = await login({ username: v.username, password: v.password, accountName: v.account });
      if (result === "ok") navigate(from, { replace: true });
    } catch (err) {
      setError(describe(err));
    }
  };

  const onMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    try {
      await completeMfa(code);
      navigate(from, { replace: true });
    } catch (err) {
      setError(describe(err));
    }
  };

  const onVonix = async () => {
    setError(undefined);
    try {
      await startPortalSignIn(from);
    } catch (err) {
      setError(err instanceof PortalSignInError ? err.message : describe(err));
    }
  };

  const onForgot = async () => {
    const v = form.getValues();
    if (!v.account || !v.username) {
      setError("Enter the account name and username first.");
      return;
    }
    try {
      await requestPasswordReset(v.username, { accountName: v.account });
      setMode("sent");
    } catch (err) {
      setError(describe(err));
    }
  };

  return (
    <div className="flex h-full items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-2">
          <span className="size-6 rounded-md bg-[linear-gradient(135deg,#83B437_0%,#E9973E_58%,#C7325D_100%)]" aria-hidden="true" />
          <div>
            <div className="text-base font-semibold leading-tight">Switchboard</div>
            <div className="text-xs text-muted-foreground">Offhook operations console</div>
          </div>
        </div>

        {pendingMfa ? (
          <form onSubmit={onMfa} className="space-y-4">
            <div>
              <h1 className="text-lg font-semibold">Second factor</h1>
              <p className="text-sm text-muted-foreground">
                Enter the code from your authenticator{pendingMfa.provider ? ` (${pendingMfa.provider})` : ""}.
              </p>
            </div>
            <Input autoFocus inputMode="numeric" autoComplete="one-time-code" placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} />
            {error && <p className="text-sm text-bad">{error}</p>}
            <Button type="submit" className="w-full" disabled={code.length < 6}>
              Verify
            </Button>
          </form>
        ) : mode === "sent" ? (
          <div className="space-y-3">
            <h1 className="text-lg font-semibold">Check your email</h1>
            <p className="text-sm text-muted-foreground">If that user exists, a reset link is on its way. It is valid for one hour.</p>
            <Button variant="outline" onClick={() => setMode("login")}>
              Back to sign in
            </Button>
          </div>
        ) : (
          <Form {...form}>
            {withVonix && (
              <div className="mb-4 space-y-3">
                <Button type="button" variant="outline" className="w-full" onClick={() => void onVonix()}>
                  <span className="mr-2 size-3 rounded-sm bg-[linear-gradient(135deg,#83B437_0%,#E9973E_58%,#C7325D_100%)]" aria-hidden="true" />
                  Sign in with Vonix
                </Button>
                <div className="flex items-center gap-2 text-2xs uppercase tracking-wide text-muted-foreground">
                  <span className="h-px flex-1 bg-border" />
                  or with a phone system account
                  <span className="h-px flex-1 bg-border" />
                </div>
              </div>
            )}
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
              <FormField
                control={form.control}
                name="account"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account name</FormLabel>
                    <FormControl>
                      <Input autoComplete="organization" autoFocus {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Username</FormLabel>
                    <FormControl>
                      <Input autoComplete="username" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="current-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {error && <p className="text-sm text-bad">{error}</p>}
              <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? "Signing in…" : "Sign in"}
              </Button>
              <button type="button" onClick={onForgot} className="w-full text-center text-xs text-muted-foreground hover:text-foreground">
                Forgot password
              </button>
            </form>
          </Form>
        )}
      </div>
    </div>
  );
}
